import React, { useEffect, useMemo, useState } from 'react';
import IsoDatePicker from './IsoDatePicker';
import { msSlotsClient } from '../utils/msClient';

export default function MSInlinePicker({
  offering,
  onPick,
  onDraftChange,
  bookingRange,
  hoursCtx,
  msRules,
  live,
  timezone,
  firstBookableTs,
  defaultDate = '',
  initialParty = 2,
  hideAdd = false,
  winStart = '',
  winEnd = '',
}) {
  const [date, setDate] = useState(defaultDate || '');
  const [party, setParty] = useState(Number(initialParty || 2));
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  const seatingConfigId = offering.ms_seating_config_id || offering.ms_seating_config || offering.id;
  let areaTypeNorm = offering.ms_area_type || offering.ms_area || offering.area_type || offering.guests_json?.ms_area_type || null;
  // try to infer from msRules.tables when missing
  if (!areaTypeNorm && msRules && seatingConfigId) {
    try {
      const tbl = (msRules.tables || []).find(t => String(t.seating_config_id) === String(seatingConfigId));
      if (tbl && tbl.area_type) areaTypeNorm = tbl.area_type;
    } catch (e) { /* ignore */ }
  }

  const includeDates = useMemo(() => {
    if (!bookingRange?.start_date || !bookingRange?.end_date) return [];
    const caps = (msRules?.capacity || []).filter(r => String(r.seating_config_id) === String(seatingConfigId));
    const hasCaps = caps.length > 0;
    const maxTotal = hasCaps ? caps.reduce((m, r) => Math.max(m, Number(r.total_size || 0)), 0) : Infinity;
    if (hasCaps && Number(party || 0) > maxTotal) return [];
    const out = [];
    const start = new Date(bookingRange.start_date);
    const end = new Date(bookingRange.end_date);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10);
      const areaType = areaTypeNorm;
      const raw = msSlotsClient({
        dateISO: iso,
        party,
        seatingConfigId,
        areaType,
        hoursCtx,
        msRules,
        live,
        firstBookableISO: firstBookableTs,
        tz: timezone
      });
      const any = Array.isArray(raw) ? raw.some(s => (!winStart && !winEnd) || (s.start >= (winStart || '00:00') && s.end <= (winEnd || '23:59'))) : false;
      if (any) out.push(iso);
    }
    return out;
  }, [bookingRange?.start_date, bookingRange?.end_date, party, seatingConfigId, offering.ms_area_type, hoursCtx, msRules, live, firstBookableTs, timezone, winStart, winEnd]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!date) { setSlots([]); setTime(''); return; }
      setLoading(true);
      const areaType = areaTypeNorm;
      const raw = msSlotsClient({
        dateISO: date,
        party,
        seatingConfigId,
        areaType,
        hoursCtx,
        msRules,
        live,
        firstBookableISO: firstBookableTs,
        tz: timezone
      });
      const s = (Array.isArray(raw) ? raw.filter(x => (!winStart && !winEnd) || (x.start >= (winStart || '00:00') && x.end <= (winEnd || '23:59'))) : []).slice(0, 60);
      if (!cancelled) {
        setSlots(s);
        if (!s.some(x => x.start === time)) setTime('');
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [date, party, offering.ms_seating_config_id, offering.ms_area_type, hoursCtx, msRules, live, timezone, firstBookableTs, winStart, winEnd]);

  // NEW: auto-refresh slots when live/schedule/filters change and date is selected
  useEffect(() => {
    if (!date) return;
    // Trigger the same effect as above by changing a no-op state or call load inline
    const areaType = areaTypeNorm;
    const raw = msSlotsClient({
      dateISO: date,
      party,
      seatingConfigId,
      areaType,
      hoursCtx,
      msRules,
      live,
      firstBookableISO: firstBookableTs,
      tz: timezone
    });
    const s = (Array.isArray(raw) ? raw.filter(x => (!winStart && !winEnd) || (x.start >= (winStart || '00:00') && x.end <= (winEnd || '23:59'))) : []).slice(0, 60);
    setSlots(s);
    if (!s.some(x => x.start === time)) setTime('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(live), JSON.stringify(msRules), party, winStart, winEnd, timezone, firstBookableTs, JSON.stringify(hoursCtx)]);

  async function add() {
    if (!date || !time) return;
    const s = slots.find(x => x.start === time);
    if (!s?.start || !s?.end) return;
    setAdding(true);
    try {
      await onPick(offering, {
        slot_start: `${date}T${s.start}:00`,
        slot_end: `${date}T${s.end}:00`,
        ms_seating_config_id: offering.ms_seating_config_id,
        ms_area_type: offering.ms_area_type,
        party
      });
    } finally { setAdding(false); }
  }

  const timeOptions = slots.map(s => s.start);

  // Emit draft when a valid time is chosen (mirrors Add enablement)
  React.useEffect(() => {
    if (typeof onDraftChange !== 'function') return;
    if (!date || !time) { try { onDraftChange(offering, null); } catch (e) {} return; }
    const s = slots.find(x => x.start === time);
    if (!s?.start || !s?.end) { try { onDraftChange(offering, null); } catch (e) {} return; }
    const draft = {
      slot_start: `${date}T${s.start}:00`,
      slot_end: `${date}T${s.end}:00`,
      ms_seating_config_id: offering.ms_seating_config_id || seatingConfigId,
      ms_area_type: offering.ms_area_type || areaTypeNorm,
      party: Math.max(1, Number(party || 1)),
    };
    try { onDraftChange(offering, draft); } catch (e) {}
  }, [onDraftChange, offering, date, time, slots, party]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-600">Date</label>
          <IsoDatePicker
            value={date}
            onChange={setDate}
            minDate={bookingRange?.start_date}
            maxDate={bookingRange?.end_date}
            includeDates={includeDates}
            includeDatesStrict
            isClearable
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600">Party</label>
          <input
            type="number" min={1}
            value={party}
            onChange={e => setParty(Number(e.target.value || 1))}
            className="border rounded px-2 py-1 w-20"
          />
        </div>

        <div>
          <label className="block text-xs text-gray-600">Time</label>
          <select
            disabled={!date || loading || !timeOptions.length}
            value={time}
            onChange={e => setTime(e.target.value)}
            className="border rounded px-2 py-1 w-32"
          >
            <option value="">{loading ? 'Loading…' : 'Select'}</option>
            {timeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {!hideAdd && (
          <button
            onClick={add}
            disabled={!date || !time || adding}
            className="ml-auto px-3 py-2 rounded bg-black text-white text-sm disabled:opacity-40"
          >
            {adding ? 'Adding…' : 'Add'}
          </button>
        )}
      </div>

      {!loading && date && !timeOptions.length && (
        <div className="text-xs text-amber-700">No tables available on this date for that party.</div>
      )}
    </div>
  );
}
